import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * KD-3581 §7 / AC09 — the six-second window in which the removal of an
 * experience can be taken back.
 *
 * The snapshot is taken by the day that performs the removal, right before it
 * touches anything, but it is held here: the frame the experience lived in
 * disappears with it, so the offer has to outlive the component that triggered
 * it. Each app's own `restore` closes over whatever it needs to bring the
 * removed experience back (dmc: the provider's flat services + the store's
 * program; to: the day's own `selectedExtras`) — this hook only owns the
 * window itself.
 *
 * Once the window closes the snapshot is dropped: the removal is final, and
 * adding the same experience again starts with no service selected.
 */
export const UNDO_WINDOW_MS = 6000;

interface ExperienceRemoval {
  startDayIndex: number;
  dayCount?: number;
  snapshot: any;
}

interface UseExperienceRemovalUndoParams {
  restore: (snapshot: any) => void;
}

export function useExperienceRemovalUndo({
  restore,
}: UseExperienceRemovalUndoParams) {
  const [pendingRemoval, setPendingRemoval] =
    useState<ExperienceRemoval | null>(null);
  // The timer callback and `undoRemoval` both need the current offer without
  // re-arming themselves on every render, so it is mirrored in a ref.
  const pendingRef = useRef<ExperienceRemoval | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const forget = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    setPendingRemoval(null);
  }, []);

  // Leaving the composer takes the pending offer with it.
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const captureRemoval = useCallback((removal: ExperienceRemoval) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    pendingRef.current = removal;
    setPendingRemoval(removal);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      pendingRef.current = null;
      setPendingRemoval(null);
    }, UNDO_WINDOW_MS);
  }, []);

  const undoRemoval = useCallback(() => {
    const removal = pendingRef.current;
    forget();

    if (removal) {
      restore(removal.snapshot);
    }
  }, [forget, restore]);

  return { pendingRemoval, captureRemoval, undoRemoval };
}
